import { create } from 'zustand';
import securePersist from '@/utils/securePersist';
import { authStore } from '@/store/authStore';
import { validateSession } from '@/utils/validateSessionRequest';

let sessionTimer: ReturnType<typeof setTimeout> | null = null;

type SessionState = {
  sessionExpired: boolean;
  isSessionModalOpen: boolean;
  expiresIn: number | null;
  expiresAt: number | null;
  setExpiresIn: (expiresIn: number | null) => void;
  setSessionExpired: (expired: boolean) => void;
  openSessionModal: () => void;
  closeSessionModal: () => void;
  scheduleSessionValidation: (expiresAt: number) => void;
};

export const sessionStore = create<SessionState>()(
  securePersist(
    (set) => ({
      sessionExpired: false,
      isSessionModalOpen: false,
      expiresIn: null,
      expiresAt: null,

      setExpiresIn: (expiresIn) => set({ expiresIn }),

      setSessionExpired: (expired) => set({ sessionExpired: expired }),

      openSessionModal: () => set({ isSessionModalOpen: true }),

      closeSessionModal: () => {
        if (sessionTimer) clearTimeout(sessionTimer);
        authStore.getState().clearTokens();
        set({
          sessionExpired: false,
          isSessionModalOpen: false,
          expiresIn: null,
          expiresAt: null,
        });
      },

      scheduleSessionValidation: (expiresAt) => {
        if (sessionTimer) clearTimeout(sessionTimer);

        const delay = expiresAt - Date.now();
        if (delay <= 0) {
          set({ sessionExpired: true, isSessionModalOpen: true });
          return;
        }

        sessionTimer = setTimeout(async () => {
          const isValid = await validateSession();
          const { expiresIn } = sessionStore.getState();

          if (isValid && expiresIn) {
            const newExpiresAt = Date.now() + expiresIn * 1000;
            sessionStore.getState().scheduleSessionValidation(newExpiresAt);
          } else {
            set({ sessionExpired: true, isSessionModalOpen: true });
          }
        }, delay);

        set({ expiresAt });
      },
    }),
    { name: 'session-store' },
  ),
);
